"use client";

import React, { useEffect, useState, JSX, useRef, useCallback } from "react";
import { SpecSection, SpecValueType } from "./specsFeature.types";
import { specsInfo } from "./specsFeature.data";
import Image from "next/image";
import {
    ISpecRange,
    MILEAGE_SPEC_KEY,
    getSpecRangeByKey,
    formatSpecRange,
} from "@/component/pdp/specRange.util";

/* ---------- Spec Value ---------- */
interface SpecValueProps {
    value: SpecValueType;
}

interface SpecsFeatureProps {
    specSections?: any;
    variants?: any[];
    specsRange?: ISpecRange[];
    mileageRange?: { min: number | null; max: number | null } | null;
    modelName?: string;
    featureSections?: any;
    title?: string;
}

function SpecValue({ value }: SpecValueProps) {
    if (value === true)
        return (
            <span className="flex items-center gap-1.5 py-1 px-2.5 rounded-[20px] text-[13px] font-semibold text-green-600 max-[768px]:p-0">
                <Image
                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/circle-check.svg`}
                    alt="circle-check"
                    title="circle-check"
                    width={6}
                    height={12}
                    className="w-4 h-4 inline-block mr-1"                    
                    />
                 Yes
            </span>
        );

    if (value === false)
        return (
            <span className="flex items-center gap-1.5 py-1 px-2.5 rounded-[20px] text-[13px] font-semibold text-red-500 max-[768px]:p-0">
                    {" "}
                    <Image
                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/circle-cross.svg`}
                    alt="circle-cross"
                    width={6}
                    height={12}
                    className="w-4 h-4 inline-block mr-1 rotate-45"                    
                    />
                     No
            </span>
        );

    return (
        <span className="text-sm font-semibold max-[768px]:text-xs max-[768px]:font-medium">
            {value}
        </span>
    );
}

/* ---------- Main Component ---------- */
export default function SpecsFeature({
    specSections,
    variants,
    specsRange,
    mileageRange,
    modelName,
    featureSections,
    title,
}: SpecsFeatureProps): JSX.Element {
    // dropdown options — variants passed in here don't carry an embedded
    // `model` ref (they come from the per-model variant list), so the model
    // name is passed in as a prop instead.
    const dropdownOptions =
        variants?.filter((variant: any) => variant?.specSections?.length || variant?.featureSections?.length)?.map((variant: any) => ({
            label: `${modelName ?? variant.model?.name ?? ''} ${variant.name}`.trim(),
            value: variant._id,
        })) || [];

    const showVariantSelector = variants?.length;

    const initialVariant = variants?.length
        ? variants?.find((variant) => variant.is_base_variant) ||
          variants?.[0] ||
          {}
        : {};

    const [selectedVariantSpecs, setSelectedVariantSpecs] = useState<any>(
        initialVariant?.specSections || specSections,
    );
    const [selectedVariantFeatures, setSelectedVariantFeatures] = useState<any>(
        initialVariant?.featureSections || featureSections,
    );
    const [active, setActive] = useState("");
    const [tooltipKey, setTooltipKey] = useState<string | null>(null);
    const [sheetData, setSheetData] = useState<{
        title: string;
        content: string;
    } | null>(null);
    const [isHoverable, setIsHoverable] = useState(true);
    // variant dropdown
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedDropdown, setSelectedDropdown] = useState(
        dropdownOptions?.find((opt) => opt.value === initialVariant._id) || {
            label: "",
            value: "",
        },
    );
    const dropdownRef = useRef<HTMLDivElement>(null);
    const mobileDropdownRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLElement>(null);
    const asideRef = useRef<HTMLElement>(null);
    const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
    const scrollSpyLocked = useRef(false);
    const skipInitialChipScrollRef = useRef(true);
    const activeRef = useRef(active);
    activeRef.current = active;

    const scrollChipIntoAside = useCallback((chip: HTMLElement, aside: HTMLElement) => {
        const chipTop = chip.offsetTop;
        const chipBottom = chipTop + chip.offsetHeight;
        const viewTop = aside.scrollTop;
        const viewBottom = viewTop + aside.clientHeight;

        if (chipTop < viewTop) {
            aside.scrollTo({ top: chipTop, behavior: "smooth" });
        } else if (chipBottom > viewBottom) {
            aside.scrollTo({
                top: chipBottom - aside.clientHeight,
                behavior: "smooth",
            });
        }
    }, []);

    // Range across all of this model's variants for a given spec key (mileage comes
    // from the dedicated mileage_range field; everything else from specs_range).
    const getRangeText = (key: string): string => {
        if (!key) return "";
        if (key.trim().toLowerCase() === MILEAGE_SPEC_KEY) {
            if (!mileageRange || (mileageRange.min == null && mileageRange.max == null)) return "";
            return formatSpecRange(mileageRange.min, mileageRange.max);
        }
        const range = getSpecRangeByKey(specsRange, key);
        if (!range || range.min == null || range.max == null || range.min === range.max) return "";
        return formatSpecRange(range.min, range.max, range.unit);
    };

    // A spec's display value comes from `value` when present (legacy/manual entries),
    // otherwise falls back to `valueNormalized` + `unit` (the standard synced shape).
    // Null/empty on both just renders blank — no substituting a different field.
    const resolveSpecValue = (s: any): SpecValueType => {
        if (s?.value !== undefined && s?.value !== null) {
            return typeof s.value === 'string' ? s.value.trim() : s.value;
        }
        if (s?.valueNormalized !== undefined && s?.valueNormalized !== null) {
            return `${s.valueNormalized}${s.unit ? ` ${s.unit}` : ''}`.trim();
        }
        return '';
    };

    // Feature availability is a plain boolean (or, rarely, a count) — no unit/range concept.
    const resolveFeatureValue = (f: any): SpecValueType => {
        const value = f?.available;

        if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))) {
            return String(value);
        }

        return Boolean(value);
    };

    // Merge spec sections (numeric measurements) and feature sections (yes/no
    // availability) into one tabbed list, ordered by each section's own `order`
    // so e.g. Engine & Transmission, Dimensions & Capacity, Comfort & Convenience,
    // Safety all appear together instead of features being a separate block.
    const mergedSections: any[] = [
        ...(selectedVariantSpecs ?? []).map((s: any) => ({ ...s, _kind: 'spec' })),
        ...(selectedVariantFeatures ?? []).map((f: any) => ({ ...f, _kind: 'feature' })),
    ].sort((a, b) => (a?.order ?? 999) - (b?.order ?? 999));

    // restructure specs feature data
    const specsFeatureData: SpecSection[] = mergedSections
        ?.map((section: any) => {
            if (section._kind === 'feature') {
                if (!section || section?.features?.length === 0) return null;
                return {
                    id: section.key,
                    label: section.label,
                    specs: section?.features?.map((f: any) => ({
                        key: f.label,
                        value: resolveFeatureValue(f),
                        info: specsInfo[f.key as keyof typeof specsInfo],
                        range: getRangeText(f.key),
                    })),
                };
            }

            if (!section || section?.specs?.length === 0) {
                return null;
            }

            return {
                id: section.key,
                label: section.label,
                specs: section?.specs?.map((s: any) => ({
                    key: s.label,
                    value: resolveSpecValue(s),
                    info: specsInfo[s.key as keyof typeof specsInfo],
                    range: getRangeText(s.key),
                })),
            };
        })
        .filter((section: any): section is SpecSection => Boolean(section));

    const sectionIdsKey = specsFeatureData.map((s) => s.id).join("|");

    useEffect(() => {
        const selectedVariant = variants?.find(
            (variant) => variant._id === selectedDropdown.value,
        );
        setSelectedVariantSpecs(selectedVariant?.specSections || specSections);
        setSelectedVariantFeatures(
            selectedVariant?.featureSections || featureSections,
        );
    }, [selectedDropdown]);

    useEffect(() => {
        if (specsFeatureData?.length) {
            setActive(specsFeatureData?.[0]?.id);
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }
        }
    }, [selectedVariantSpecs, selectedVariantFeatures]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(hover: hover)");
        setIsHoverable(mq.matches);
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const inDropdown = dropdownRef.current?.contains(target);
            const inMobileDropdown = mobileDropdownRef.current?.contains(target);
            if (!inDropdown && !inMobileDropdown) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownOpen]);

    const syncActiveFromScroll = useCallback(() => {
        if (scrollSpyLocked.current) return;

        const root = scrollContainerRef.current;
        if (!root || !sectionIdsKey) return;

        const sectionIds = sectionIdsKey.split("|");
        const anchor = root.getBoundingClientRect().top + 8;
        let nextActive = sectionIds[0];

        for (const id of sectionIds) {
            const el = sectionRefs.current[id];
            if (!el) continue;
            if (el.getBoundingClientRect().top <= anchor) {
                nextActive = id;
            }
        }

        if (nextActive !== activeRef.current) {
            setActive(nextActive);
        }
    }, [sectionIdsKey]);

    useEffect(() => {
        const root = scrollContainerRef.current;
        if (!root || !sectionIdsKey) return;

        let rafId = 0;
        const onScroll = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(syncActiveFromScroll);
        };

        root.addEventListener("scroll", onScroll, { passive: true });
        syncActiveFromScroll();

        return () => {
            root.removeEventListener("scroll", onScroll);
            cancelAnimationFrame(rafId);
        };
    }, [sectionIdsKey, syncActiveFromScroll]);

    useEffect(() => {
        if (scrollSpyLocked.current || !active || !asideRef.current) return;
        if (skipInitialChipScrollRef.current) {
            skipInitialChipScrollRef.current = false;
            return;
        }
        const aside = asideRef.current;
        const chip = aside.querySelector<HTMLElement>(
            `[data-chip-id="${CSS.escape(active)}"]`,
        );
        if (chip) scrollChipIntoAside(chip, aside);
    }, [active, scrollChipIntoAside]);

    const releaseScrollSpyLock = useCallback(
        (root: HTMLElement) => {
            const release = () => {
                scrollSpyLocked.current = false;
                syncActiveFromScroll();
            };

            if ("onscrollend" in root) {
                root.addEventListener("scrollend", release, { once: true });
                return;
            }

            window.setTimeout(release, 450);
        },
        [syncActiveFromScroll],
    );

    const handleChipClick = useCallback(
        (id: string) => {
            const root = scrollContainerRef.current;
            const el = sectionRefs.current[id];
            if (!root || !el) {
                setActive(id);
                return;
            }

            scrollSpyLocked.current = true;
            setActive(id);

            const top =
                el.getBoundingClientRect().top -
                root.getBoundingClientRect().top +
                root.scrollTop;

            root.scrollTo({ top, behavior: "smooth" });
            releaseScrollSpyLock(root);
        },
        [releaseScrollSpyLock],
    );

    const renderSpecTable = (section: SpecSection) => (
        <table className="w-full border-collapse">
            <tbody>
                {section.specs.map((spec) => (
                    <tr
                        key={`${section.id}-${spec.key}`}
                        className="border-b border-[#e5e7eb] last:border-b-0 max-[768px]:flex max-[768px]:flex-col max-[768px]:items-start"
                    >
                        <td className="py-3.5 px-5 text-sm text-[#6b7280] align-middle max-[768px]:pt-3 max-[768px]:px-4 max-[768px]:pb-0 max-[768px]:text-[13px] max-[768px]:font-normal max-[768px]:block max-[768px]:w-full">
                            <span className="inline-flex items-center gap-1.5">
                                {spec.key}

                                {spec.info && (
                                    <span
                                        className="flex justify-center items-center cursor-pointer text-[#9ca3af] border-[1.7px] border-solid border-gray-400 rounded-[20px] w-3.5 h-3.5 text-[8px] relative"
                                        onMouseEnter={() =>
                                            isHoverable &&
                                            setTooltipKey(`${section.id}-${spec.key}`)
                                        }
                                        onMouseLeave={() =>
                                            isHoverable && setTooltipKey(null)
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isHoverable) {
                                                setSheetData({
                                                    title: spec.key,
                                                    content: spec.info,
                                                });
                                            }
                                        }}
                                    >
                                        i
                                        {isHoverable &&
                                            tooltipKey ===
                                                `${section.id}-${spec.key}` && (
                                                <span className="absolute z-10 top-3 left-0 text-black bg-white rounded-[9px] min-w-61.25 p-3 text-[13px] shadow-[0_0_8.4px_#0000001f]">
                                                    {spec.info}
                                                </span>
                                            )}
                                    </span>
                                )}
                            </span>
                        </td>
                        <td className="py-3.5 px-5 text-right align-middle max-[768px]:pt-2 max-[768px]:px-4 max-[768px]:pb-3 max-[768px]:text-left max-[768px]:block max-[768px]:w-full">
                            <SpecValue value={spec.value} />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const variantDropdown = showVariantSelector ? (
        <div className="relative w-full md:w-auto" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex w-full md:w-56 items-center justify-between gap-4 rounded-xl border border-blue-100 bg-[#f3f8ff] px-4 py-3 md:py-2 text-sm font-medium text-blue-600 cursor-pointer"
            >
                <span className="line-clamp-1 text-left">{selectedDropdown?.label}</span>
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    className="text-blue-500 shrink-0"
                >
                    <path
                        d="M6 9l6 6 6-6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                    />
                </svg>
            </button>

            {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-full md:w-56 rounded-xl bg-white shadow-xl z-20 max-h-80 overflow-y-auto">
                    {dropdownOptions?.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                                setSelectedDropdown(opt);
                                setDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left text-sm transition-colors cursor-pointer ${
                                selectedDropdown.value === opt.value
                                    ? "bg-blue-50 text-blue-700 font-semibold"
                                    : "text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    ) : null;

    return (
        <>
            {title && (
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                    <h2 className="text-xl font-bold">{title}</h2>
                    {variantDropdown}
                </div>
            )}

            {/* ================= Dropdown Mobile ================= */}
            {!title && showVariantSelector && (
                <div className="mt-3 md:mt-0 md:px-5 md:py-2 w-full md:w-auto md:hidden">
                    <div className="relative" ref={mobileDropdownRef}>
                        <button
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                            className="flex w-full md:w-auto items-center justify-between md:justify-start gap-4 rounded-xl border border-blue-100 bg-[#f3f8ff] px-4 py-3 md:py-2 text-sm font-medium text-blue-600 cursor-pointer"
                        >
                            {selectedDropdown?.label}
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                className="text-blue-500"
                            >
                                <path
                                    d="M6 9l6 6 6-6"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                />
                            </svg>
                        </button>

                        {dropdownOpen && (
                            <div className="absolute right-0 mt-2 w-full md:w-56 rounded-xl  bg-white shadow-xl z-20 max-h-80 overflow-y-auto">
                                {dropdownOptions?.map((opt) => (
                                    <button
                                        key={opt.value}
                                        onClick={() => {
                                            setSelectedDropdown(opt);
                                            setDropdownOpen(false);
                                        }}
                                        className={`w-full px-4 py-3 text-left text-sm transition-colors cursor-pointer ${
                                            selectedDropdown.value === opt.value
                                                ? "bg-blue-50 text-blue-700 font-semibold"
                                                : "text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className="flex max-h-[55vh] md:max-h-[600px] max-[768px]:-mx-4 border border-[#e5e7eb] rounded-xl overflow-hidden bg-neutral-50 no-scrollbar hidden-scrollbar">
                {/* Sticky section chips */}
                <aside
                    ref={asideRef}
                    className="bg-[#155dfc05] w-55 p-3 max-[768px]:w-41.25 shrink-0 overflow-y-auto sticky top-0 self-start max-h-[55vh] md:max-h-[600px]"
                >
                    {specsFeatureData?.map((section) => (
                        <button
                            key={section.id}
                            type="button"
                            data-chip-id={section.id}
                            className={`text-left cursor-pointer bg-transparent border-none w-full py-2.5 px-4.5 text-sm hover:bg-gray-100 hover:rounded-xl max-[768px]:p-3 max-[768px]:text-[11px] ${
                                active === section.id
                                    ? "text-[#0f7d5a] bg-[#e6f4ef] rounded-xl font-semibold"
                                    : "text-[#6b7280]"
                            }`}
                            onClick={() => handleChipClick(section.id)}
                        >
                            {section.label}
                        </button>
                    ))}
                </aside>

                {/* Vertically scrollable specs — all sections */}
                <main
                    ref={scrollContainerRef}
                    className="bg-neutral-50 flex-1 overflow-y-auto overscroll-contain"
                >
                    {specsFeatureData.map((section, index) => (
                        <div
                            key={section.id}
                            data-section-id={section.id}
                            ref={(el) => {
                                sectionRefs.current[section.id] = el;
                            }}
                            className="bg-neutral-50"
                        >
                            <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-[#e5e7eb]">
                                <h2 className="p-5 text-lg font-semibold max-[768px]:p-4 max-[768px]:text-sm">
                                    {section.label}
                                </h2>

                                {!title && showVariantSelector && index === 0 && (
                                    <div className="mt-3 md:mt-0 md:px-5 md:py-2 w-full md:w-auto hidden md:block">
                                        {variantDropdown}
                                    </div>
                                )}
                            </div>
                            {renderSpecTable(section)}
                        </div>
                    ))}
                </main>
            </div>

            {/* -------- Bottom Sheet (Mobile) -------- */}
            {sheetData && (
                <>
                    <div
                        className="fixed inset-0 z-9999 bg-black/40"
                        onClick={() => setSheetData(null)}
                    />
                    <div className="fixed bottom-0 left-0 right-0 z-9999 bg-white rounded-t-[20px] p-2 max-[768px]:text-[#24272cb3] max-[768px]:text-left max-[768px]:border-t max-[768px]:border-t-[#24272c26] max-[768px]:rounded-t-2xl max-[768px]:pt-4 max-[768px]:px-5 max-[768px]:pb-6 max-[768px]:leading-4.875 max-[768px]:shadow-[0_-4px_14px_#0000001a]">
                        <button
                            className="max-[768px]:text-center max-[768px]:cursor-pointer max-[768px]:text-black max-[768px]:text-sm max-[768px]:font-extrabold max-[768px]:leading-6 max-[768px]:absolute max-[768px]:top-2.5 max-[768px]:right-5"
                            onClick={() => setSheetData(null)}
                        >
                            ✕
                        </button>
                        <h4 className="max-[768px]:text-[#24272c] max-[768px]:font-bold max-[768px]:!pr-[26px] max-[768px]:!text-[14px]">
                            {sheetData.title}
                        </h4>
                        <p className="max-[768px]:text-black max-[768px]:mt-2 max-[768px]:text-[13px] max-[768px]:relative">
                            {sheetData.content}
                        </p>
                    </div>
                </>
            )}
        </>
    );
}
